import type { FC, ReactNode } from 'react'
import type { Post } from '@/types'

import { useState } from 'react'
import { Loader2 } from 'lucide-react'

import { PostCard } from '@/components/blog/post-card'

interface HubPostsGridProps {
    hubSlug: string
    initialPosts: Post[]
    total: number
    excludeSlug?: string
    pageSize?: number
}

export const HubPostsGrid: FC<HubPostsGridProps> = ({
    hubSlug,
    initialPosts,
    total,
    excludeSlug,
    pageSize = 9
}): ReactNode => {
    const [posts, setPosts] = useState<Post[]>(initialPosts)
    const [loading, setLoading] = useState(false)

    const hasMore = posts.length < total

    const loadMore = async () => {
        setLoading(true)

        try {
            const params = new URLSearchParams({
                hub: hubSlug,
                offset: String(posts.length),
                limit: String(pageSize)
            })

            if (excludeSlug) params.set('exclude', excludeSlug)

            const response = await fetch(`/api/posts?${params.toString()}`)
            const data = await response.json()

            setPosts(previous => [...previous, ...(data.posts ?? [])])
        } catch (error) {
            console.error('Failed to load more posts', error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div>
            <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                {posts.map((post, index) => (
                    <PostCard key={post.slug} post={post} priority={index < 3} />
                ))}
            </div>

            {hasMore && (
                <div className='mt-10 flex justify-center'>
                    <button
                        type='button'
                        onClick={loadMore}
                        disabled={loading}
                        className='inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-primary/50 hover:text-primary disabled:opacity-60'
                    >
                        {loading && <Loader2 className='size-4 animate-spin' />}
                        {loading ? 'Loading…' : 'Load more articles'}
                    </button>
                </div>
            )}
        </div>
    )
}
